import type { Phase } from '../derive-phase';
import type { GroupTiming, ResolvedTask } from './types';

// Months before the wedding a period points at. `P` (the day) is 0 and `+P` (after it)
// is -1: markers, not month counts.
export const periodMonthsBefore = (period: string): number | null => {
  if (period === 'P') {
    return 0;
  }
  if (period === '+P') {
    return -1;
  }
  if (period === '-P1W') {
    return 0.25;
  }
  const match = period.match(/^-P(\d+)([MW])$/);
  if (!match) {
    // '-P' — the template's "anytime", i.e. not resolved against a date.
    return null;
  }
  return match[2] === 'W' ? Number.parseInt(match[1], 10) / 4 : Number.parseInt(match[1], 10);
};

// Same boundaries as the couple's own phase, so a group's phase and the couple's phase
// use one scale and cannot disagree.
export const phaseForMonthsBefore = (months: number): Phase => {
  if (months <= 0) {
    return 'DayAndAfter';
  }
  if (months < 4) {
    return 'FinalCountdown';
  }
  if (months < 7) {
    return 'DetailPlanning';
  }
  if (months < 13) {
    return 'CoreBooking';
  }
  return 'EarlyPlanning';
};

const PRE_WEDDING_PHASES: Phase[] = [
  'EarlyPlanning',
  'CoreBooking',
  'DetailPlanning',
  'FinalCountdown',
];

const quantile = (sorted: number[], p: number): number =>
  sorted.length === 0 ? 0 : sorted[Math.floor(p * (sorted.length - 1))];

/**
 * Phase and timing per group, from the couple's resolved periods. A group starts when
 * its earliest task does, so it takes the phase of its furthest-out task.
 *
 * With no wedding date, groups are split into quartiles by template `order`. This is a
 * last resort, because the boundaries move when the template changes.
 */
export const buildGroupTimings = (tasks: readonly ResolvedTask[]): Map<string, GroupTiming> => {
  const byGroup = new Map<string, ResolvedTask[]>();
  for (const task of tasks) {
    const list = byGroup.get(task.group) ?? [];
    list.push(task);
    byGroup.set(task.group, list);
  }

  const dated = new Map<string, { months: number; onOrAfterDay: boolean }>();
  const undatedOrders: { groupId: string; minOrder: number }[] = [];

  for (const [groupId, groupTasks] of byGroup) {
    const months = groupTasks
      .map((task) => periodMonthsBefore(task.period))
      .filter((value): value is number => value != null);

    if (months.length === 0) {
      undatedOrders.push({
        groupId,
        minOrder: Math.min(...groupTasks.map((task) => task.order)),
      });
      continue;
    }
    dated.set(groupId, {
      months: Math.max(...months),
      onOrAfterDay: months.every((value) => value <= 0),
    });
  }

  const timings = new Map<string, GroupTiming>();
  for (const [groupId, { months, onOrAfterDay }] of dated) {
    timings.set(groupId, {
      phase: onOrAfterDay ? 'DayAndAfter' : phaseForMonthsBefore(months),
      monthsBeforeWedding: months,
    });
  }

  const orders = undatedOrders.map((entry) => entry.minOrder).sort((a, b) => a - b);
  const bounds = [0.25, 0.5, 0.75].map((p) => quantile(orders, p));
  for (const { groupId, minOrder } of undatedOrders) {
    const index = bounds.findIndex((bound) => minOrder <= bound);
    timings.set(groupId, {
      phase: PRE_WEDDING_PHASES[index === -1 ? 3 : index],
      monthsBeforeWedding: null,
    });
  }

  return timings;
};
